import React from 'react'
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_500, GRAY_700, GRAY_900, GRAY_BORDER } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { formatPrice } from 'utils/price'

/** One bookable creative. */
export interface CreativesTableRow {
  id: string
  fullName: string
  phoneNumber?: string
  /** What their distance adds to the order; shown as "-" when unknown. */
  travelCost?: { amount: number, currency: string }
}

interface Props {
  /** Section heading above the table. */
  title: string
  /** The bucket's creatives. An empty bucket renders the no-results line, not an
   *  empty table — the heading still tells the client the bucket was searched. */
  creatives: ReadonlyArray<CreativesTableRow>
  onChoose: (id: string) => void
}

const cellSx = {
  fontSize: '1.4rem',
  textAlign: 'left',
  verticalAlign: 'middle',
  padding: '1.5rem 2rem 1.5rem 0',
  borderBottom: `1px solid ${GRAY_BORDER}`,
  color: GRAY_900,
}

const headCellSx = {
  ...cellSx,
  padding: '0 2rem 1rem 0',
  fontSize: '1.2rem',
  fontWeight: 500,
  color: GRAY_700,
  borderBottom: `1px solid ${GRAY_700}`,
}

/**
 * One bucket of bookable creatives as a table: name, phone, travel cost and a choose button.
 *
 * @example
 * <CreativesTable title={t('…personal_title')} creatives={buckets.personal} onChoose={onChange} />
 */
export const CreativesTable: React.FC<Props> = ({ title, creatives, onChoose }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  return (
    <div>

      <Typography variant="text-md" color={GRAY_900} fontWeight={500} display="block" marginBottom={1.2}>
        {title}
      </Typography>

      {creatives.length === 0 &&
        <Typography variant="text-sm" color={GRAY_500}>
          {t('purchase_flow_revamp:instructions.ct_booking.no_results')}
        </Typography>
      }

      {creatives.length > 0 &&
        <Table sx={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>

          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>{t('purchase_flow_revamp:instructions.ct_booking.table_name')}</TableCell>
              <TableCell sx={headCellSx}>{t('purchase_flow_revamp:instructions.ct_booking.table_phone')}</TableCell>
              <TableCell sx={headCellSx}>{t('purchase_flow_revamp:instructions.ct_booking.travel_cost')}</TableCell>
              <TableCell sx={headCellSx} />
            </TableRow>
          </TableHead>

          <TableBody>
            {creatives.map((creative) => (
              <TableRow key={creative.id}>

                <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{creative.fullName}</TableCell>

                <TableCell sx={cellSx}>{creative.phoneNumber || '-'}</TableCell>

                <TableCell sx={cellSx}>
                  {creative.travelCost
                    ? formatPrice(creative.travelCost.amount, creative.travelCost.currency)
                    : '-'
                  }
                </TableCell>

                <TableCell sx={{ ...cellSx, padding: 0, textAlign: 'right' }}>
                  <MUIButton type="secondaryBorder" size="sm" onClick={() => onChoose(creative.id)}>
                    {t('purchase_flow_revamp:instructions.ct_booking.choose')}
                  </MUIButton>
                </TableCell>

              </TableRow>
            ))}
          </TableBody>

        </Table>
      }

    </div>
  )
}
